package com.trevorwiebe.timesheet.loads.presentation.driver

import androidx.compose.foundation.clickable
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.CircleShape
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.material3.Button
import androidx.compose.material3.ButtonDefaults
import androidx.compose.material3.CircularProgressIndicator
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.draw.clip
import androidx.compose.ui.unit.dp
import com.trevorwiebe.timesheet.loads.domain.model.LoadAttachment
import com.trevorwiebe.timesheet.loads.presentation.DocumentViewer
import com.trevorwiebe.timesheet.mileage.presentation.rememberImagePickerLauncher
import org.jetbrains.compose.resources.painterResource
import timesheet.composeapp.generated.resources.Res
import timesheet.composeapp.generated.resources.baseline_note_add_24

/** The driver's scans on one load, a Scan document button, and a full-screen document viewer. */
@Composable
internal fun DriverLoadDocuments(
    loadId: String,
    documents: List<LoadAttachment>,
    documentPdfs: Map<String, ByteArray>,
    isSaving: Boolean,
    error: String?,
    onEvent: (DriverLoadsEvent) -> Unit,
) {
    var openUrl by remember { mutableStateOf<String?>(null) }

    val scan = rememberImagePickerLauncher { pages ->
        onEvent(DriverLoadsEvent.OnScanDocument(loadId, pages))
    }

    Row(modifier = Modifier.fillMaxWidth(), verticalAlignment = Alignment.Bottom) {
        Column(modifier = Modifier.weight(1f)) {
            documents.forEach { document ->
                Text(
                    text = document.name,
                    style = MaterialTheme.typography.bodyMedium,
                    color = MaterialTheme.colorScheme.tertiary,
                    modifier = Modifier
                        .fillMaxWidth()
                        .clip(RoundedCornerShape(4.dp))
                        .clickable {
                            onEvent(DriverLoadsEvent.OnOpenDocument(loadId, document.url))
                            openUrl = document.url
                        }
                        .padding(vertical = 6.dp),
                )
            }
            error?.let {
                Text(it, style = MaterialTheme.typography.bodySmall, color = MaterialTheme.colorScheme.error)
            }
            if (isSaving) SavingRow()
        }
        Button(
            onClick = { scan() },
            enabled = !isSaving,
            shape = CircleShape,
            colors = ButtonDefaults.buttonColors(
                containerColor = MaterialTheme.colorScheme.tertiary,
                contentColor = MaterialTheme.colorScheme.onTertiary,
                disabledContainerColor = MaterialTheme.colorScheme.tertiaryContainer,
            ),
        ) {
            Icon(
                painter = painterResource(Res.drawable.baseline_note_add_24),
                contentDescription = null,
                modifier = Modifier.size(18.dp),
            )
            Text("Scan document", maxLines = 1, softWrap = false, modifier = Modifier.padding(start = 8.dp))
        }
    }

    val open = documents.find { it.url == openUrl }
    if (open != null) {
        DocumentViewer(
            document = open,
            pdf = documentPdfs[open.url],
            isSaving = isSaving,
            onDelete = {
                onEvent(DriverLoadsEvent.OnDeleteDocument(loadId, open.url))
                openUrl = null
            },
            onDismiss = { openUrl = null },
        )
    }
}

@Composable
private fun SavingRow() {
    Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.padding(vertical = 8.dp)) {
        CircularProgressIndicator(
            modifier = Modifier.size(20.dp),
            strokeWidth = 2.dp,
            color = MaterialTheme.colorScheme.tertiary,
        )
        Text("Saving…", modifier = Modifier.padding(start = 8.dp))
    }
}
